import React from "react";
import { Helmet } from "react-helmet-async";

export default function PlayRyvenoxGaming() {
	const titlePage =
		"PlayRyvenox Gaming | Plateforme communautaire gaming sur mesure | Weblax Studio";
	const description =
		"Weblax a conçu et développé PlayRyvenox Gaming, une plateforme communautaire gaming complète : profils joueurs, tournois en ligne, système de classement, live events et intégration Discord. +2 800 membres actifs en 3 mois.";
	const url = "https://weblax.fr/realisations/playryvenox-gaming";

	const features = [
		{
			icon: "👤",
			title: "Profils joueurs & stats",
			desc: "Chaque membre dispose d'un profil personnalisé avec ses statistiques, historique de parties, badges obtenus et classement communautaire — un vrai passeport gaming.",
		},
		{
			icon: "🏆",
			title: "Tournois & compétitions",
			desc: "Système de gestion de tournois complet : inscriptions, brackets automatiques, résultats en temps réel, arbitrage et remise des prix. Pour tous les niveaux et tous les jeux.",
		},
		{
			icon: "📊",
			title: "Classements dynamiques",
			desc: "Leaderboards en temps réel par jeu, par période et par catégorie. La compétition saine qui pousse les membres à revenir chaque semaine pour gratter des places.",
		},
		{
			icon: "🎮",
			title: "Événements live",
			desc: "Module d'événements communautaires : game nights, streams communautaires, soirées découverte de jeux — avec système d'inscription et rappels automatiques.",
		},
		{
			icon: "💬",
			title: "Intégration Discord",
			desc: "Synchronisation bidirectionnelle avec le serveur Discord de la communauté : rôles automatiques selon le rang, notifications d'événements et vérification de compte.",
		},
		{
			icon: "🛡️",
			title: "Modération & sécurité",
			desc: "Système de signalement, outils de modération pour les admins, gestion des sanctions et protection contre les comportements toxiques — pour maintenir une communauté saine.",
		},
	];

	const stack = [
		{ name: "Next.js", desc: "Frontend & SSR" },
		{ name: "TypeScript", desc: "Fiabilité du code" },
		{ name: "Supabase", desc: "BDD & temps réel" },
		{ name: "Discord API", desc: "Intégration communauté" },
		{ name: "Tailwind CSS", desc: "Design system" },
		{ name: "Framer Motion", desc: "Animations gaming" },
		{ name: "Vercel", desc: "Déploiement edge" },
		{ name: "Socket.io", desc: "Temps réel tournois" },
	];

	const results = [
		{
			icon: "👥",
			metric: "2 800+",
			label: "Membres actifs",
			color: "text-violet-600",
			bg: "from-violet-50 to-purple-50",
			border: "border-violet-100",
		},
		{
			icon: "🏆",
			metric: "140+",
			label: "Tournois organisés",
			color: "text-amber-600",
			bg: "from-amber-50 to-yellow-50",
			border: "border-amber-100",
		},
		{
			icon: "⏱️",
			metric: "28 min",
			label: "Temps moyen par session",
			color: "text-blue-600",
			bg: "from-blue-50 to-sky-50",
			border: "border-blue-100",
		},
		{
			icon: "💬",
			metric: "92%",
			label: "Taux de rétention M+1",
			color: "text-emerald-600",
			bg: "from-emerald-50 to-green-50",
			border: "border-emerald-100",
		},
	];

	const timeline = [
		{
			step: "01",
			title: "Cadrage & architecture",
			desc: "Définition des fonctionnalités core, architecture technique de la plateforme communautaire, choix du stack et design system gaming.",
			tag: "Semaines 1-2",
		},
		{
			step: "02",
			title: "Design & identité gaming",
			desc: "Création de l'identité visuelle dark mode, animations néon, design des composants UI et maquettes interactives validées avec l'équipe PlayRyvenox.",
			tag: "Semaines 3-4",
		},
		{
			step: "03",
			title: "Développement core",
			desc: "Développement des profils joueurs, système d'authentification, intégration Discord, classements dynamiques et module événements.",
			tag: "Semaines 5-8",
		},
		{
			step: "04",
			title: "Tournois & temps réel",
			desc: "Développement du système de tournois avec brackets automatiques, gestion des résultats en temps réel et notifications push.",
			tag: "Semaines 9-11",
		},
		{
			step: "05",
			title: "Tests & lancement",
			desc: "Tests de charge, beta test avec 50 membres fondateurs, corrections et lancement officiel avec campagne Discord.",
			tag: "Semaines 12-13",
		},
	];

	return (
		<>
			<Helmet>
				<title>{titlePage}</title>
				<meta name="description" content={description} />
				<link rel="canonical" href={url} />
				<meta property="og:type" content="website" />
				<meta property="og:title" content={titlePage} />
				<meta property="og:description" content={description} />
				<meta property="og:url" content={url} />
				<script type="application/ld+json">
					{JSON.stringify({
						"@context": "https://schema.org",
						"@type": "CreativeWork",
						name: "PlayRyvenox Gaming — Plateforme communautaire gaming",
						description,
						url,
						dateCreated: "2024",
						provider: {
							"@type": "Organization",
							name: "Weblax",
							url: "https://weblax.fr",
						},
						programmingLanguage: ["TypeScript", "Next.js"],
					})}
				</script>
			</Helmet>

			{/* ── Hero ── */}
			<section className="relative overflow-hidden bg-gradient-to-br from-violet-950 via-slate-900 to-slate-950 border-b border-violet-800/30">
				<div
					className="absolute inset-0 opacity-30"
					style={{
						backgroundImage:
							"radial-gradient(circle at 20% 50%, #7c3aed 0%, transparent 40%), radial-gradient(circle at 80% 20%, #2563eb 0%, transparent 40%)",
					}}
				/>
				<div className="weblax-container py-20 relative">
					<div className="flex flex-wrap items-center gap-3 mb-6">
						<span className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-full bg-violet-500/20 text-violet-300 border border-violet-500/30">
							🎮 Plateforme gaming
						</span>
						<span className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-full bg-green-500/20 text-green-300 border border-green-500/30">
							✅ Projet livré
						</span>
						<span className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-full bg-blue-500/20 text-blue-300 border border-blue-500/30">
							👥 Communauté active
						</span>
					</div>

					<h1 className="text-4xl md:text-5xl font-bold text-white mb-6 max-w-4xl leading-tight">
						PlayRyvenox Gaming — La plateforme qui{" "}
						<span className="text-violet-400">fédère une communauté</span> de
						joueurs passionnés
					</h1>

					<p className="text-lg text-slate-300 max-w-3xl leading-relaxed mb-10">
						Weblax a conçu et développé PlayRyvenox Gaming, une plateforme
						communautaire complète dédiée aux gamers — avec profils joueurs,
						tournois compétitifs, classements en temps réel et intégration
						Discord. Plus de 2 800 membres actifs en trois mois.
					</p>

					<div className="grid grid-cols-2 md:grid-cols-4 gap-4">
						{results.map((r) => (
							<div
								key={r.label}
								className="bg-white/5 backdrop-blur border border-white/10 rounded-xl p-5 text-center"
							>
								<div className="text-2xl mb-1">{r.icon}</div>
								<div className="text-3xl font-bold text-white mb-1">
									{r.metric}
								</div>
								<div className="text-xs text-slate-400">{r.label}</div>
							</div>
						))}
					</div>
				</div>
			</section>

			<main className="weblax-container py-20 space-y-24">
				{/* ── Contexte ── */}
				<section>
					<div className="flex items-center gap-3 mb-6">
						<span className="w-8 h-px bg-violet-500" />
						<span className="text-xs font-semibold text-violet-600 uppercase tracking-widest">
							Contexte
						</span>
					</div>
					<h2 className="text-3xl font-bold mb-6">
						Une communauté gaming dispersée, sans hub central
					</h2>
					<div className="grid md:grid-cols-2 gap-10">
						<div className="space-y-4 text-muted-foreground leading-relaxed">
							<p>
								PlayRyvenox Gaming existait avant tout comme un serveur Discord
								actif, avec une communauté soudée de joueurs passionnés. Mais
								sans plateforme propre, la croissance se heurtait à des limites
								structurelles : impossible d'organiser des tournois complexes,
								de maintenir des classements officiels ou d'offrir aux membres
								une identité de joueur au-delà de leur pseudo Discord.
							</p>
							<p>
								L'ambition de PlayRyvenox : devenir une vraie destination
								gaming, avec une plateforme qui centralise la vie de la
								communauté, récompense l'engagement et permet d'organiser des
								compétitions à grande échelle.
							</p>
							<p>
								La complexité du projet résidait dans la nécessité de créer une
								expérience suffisamment engageante pour que les membres migrent
								naturellement du Discord vers la plateforme — sans perdre ce qui
								faisait la force de la communauté.
							</p>
						</div>
						<div className="space-y-3">
							<div className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-4">
								Limitations avant la plateforme
							</div>
							{[
								{
									label: "Gestion des tournois",
									value: "Manuelle & chronophage",
									color: "text-red-500",
								},
								{
									label: "Profils et stats joueurs",
									value: "Inexistants",
									color: "text-red-500",
								},
								{
									label: "Classements officiels",
									value: "Impossibles à maintenir",
									color: "text-red-500",
								},
								{
									label: "Identité de marque digitale",
									value: "Limitée au Discord",
									color: "text-orange-500",
								},
								{
									label: "Monétisation & sponsors",
									value: "Bloquée sans plateforme",
									color: "text-orange-500",
								},
							].map((item) => (
								<div
									key={item.label}
									className="flex items-center justify-between p-4 rounded-xl border border-border bg-muted/30"
								>
									<span className="text-sm text-muted-foreground">
										{item.label}
									</span>
									<span className={`text-sm font-semibold ${item.color}`}>
										{item.value}
									</span>
								</div>
							))}
						</div>
					</div>
				</section>

				{/* ── Brief ── */}
				<section className="bg-gradient-to-r from-violet-50 to-blue-50 border border-violet-100 rounded-2xl p-10">
					<div className="flex items-center gap-3 mb-6">
						<span className="w-8 h-px bg-violet-500" />
						<span className="text-xs font-semibold text-violet-600 uppercase tracking-widest">
							Objectifs
						</span>
					</div>
					<h2 className="text-2xl font-bold mb-8">
						Ce que PlayRyvenox attendait de Weblax
					</h2>
					<div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
						{[
							{
								icon: "🏆",
								text: "Un système de tournois complet, automatisé et scalable",
							},
							{
								icon: "👤",
								text: "Des profils joueurs riches avec stats, historique et badges",
							},
							{
								icon: "📊",
								text: "Des classements dynamiques qui boostent la rétention",
							},
							{
								icon: "🎮",
								text: "Un module événements pour les game nights et streams",
							},
							{
								icon: "💬",
								text: "Une intégration Discord fluide et bidirectionnelle",
							},
							{
								icon: "🚀",
								text: "Une plateforme capable de scaler avec la croissance de la communauté",
							},
						].map((item) => (
							<div
								key={item.text}
								className="flex items-start gap-3 bg-white rounded-xl p-4 border border-violet-100 shadow-sm"
							>
								<span className="text-xl shrink-0">{item.icon}</span>
								<span className="text-sm text-muted-foreground leading-relaxed">
									{item.text}
								</span>
							</div>
						))}
					</div>
				</section>

				{/* ── Fonctionnalités ── */}
				<section>
					<div className="flex items-center gap-3 mb-6">
						<span className="w-8 h-px bg-violet-500" />
						<span className="text-xs font-semibold text-violet-600 uppercase tracking-widest">
							Solution
						</span>
					</div>
					<h2 className="text-3xl font-bold mb-4">
						Une plateforme gaming complète, pensée pour l'engagement
					</h2>
					<p className="text-muted-foreground max-w-3xl mb-10 leading-relaxed">
						Chaque fonctionnalité a été conçue pour répondre à une question
						centrale : pourquoi un joueur reviendrait-il demain ? La réponse
						passe par la progression, la compétition, la reconnaissance sociale
						et l'appartenance à une communauté active.
					</p>
					<div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
						{features.map((f) => (
							<div
								key={f.title}
								className="group p-6 rounded-2xl border border-border hover:border-violet-200 hover:shadow-md transition-all duration-200 bg-white"
							>
								<span className="text-3xl mb-4 block">{f.icon}</span>
								<h3 className="font-semibold text-foreground mb-2">
									{f.title}
								</h3>
								<p className="text-sm text-muted-foreground leading-relaxed">
									{f.desc}
								</p>
							</div>
						))}
					</div>
				</section>

				{/* ── Timeline ── */}
				<section>
					<div className="flex items-center gap-3 mb-6">
						<span className="w-8 h-px bg-violet-500" />
						<span className="text-xs font-semibold text-violet-600 uppercase tracking-widest">
							Développement
						</span>
					</div>
					<h2 className="text-3xl font-bold mb-10">
						13 semaines de développement intensif
					</h2>
					<div className="relative">
						<div className="absolute left-6 top-0 bottom-0 w-px bg-gradient-to-b from-violet-200 via-violet-300 to-transparent hidden md:block" />
						<div className="space-y-6">
							{timeline.map((item, i) => (
								<div key={item.step} className="relative flex gap-6">
									<div className="shrink-0 w-12 h-12 rounded-full bg-violet-100 border-2 border-violet-200 flex items-center justify-center text-violet-600 font-bold text-sm z-10">
										{item.step}
									</div>
									<div className="flex-1 pb-6">
										<div className="flex flex-wrap items-center gap-3 mb-1">
											<span className="text-xs font-medium text-violet-500">
												{item.tag}
											</span>
											<h3 className="font-semibold text-foreground">
												{item.title}
											</h3>
										</div>
										<p className="text-sm text-muted-foreground leading-relaxed">
											{item.desc}
										</p>
									</div>
								</div>
							))}
						</div>
					</div>
				</section>

				{/* ── Stack ── */}
				<section className="bg-muted/30 rounded-2xl border border-border p-10">
					<div className="flex items-center gap-3 mb-6">
						<span className="w-8 h-px bg-violet-500" />
						<span className="text-xs font-semibold text-violet-600 uppercase tracking-widest">
							Stack technique
						</span>
					</div>
					<h2 className="text-2xl font-bold mb-8">
						Technologies choisies pour scaler
					</h2>
					<div className="flex flex-wrap gap-3">
						{stack.map((tech) => (
							<div
								key={tech.name}
								className="flex items-center gap-2 bg-white border border-border rounded-lg px-4 py-2.5 shadow-sm"
							>
								<span className="font-semibold text-sm text-foreground">
									{tech.name}
								</span>
								<span className="text-xs text-muted-foreground">
									— {tech.desc}
								</span>
							</div>
						))}
					</div>
				</section>

				{/* ── Résultats ── */}
				<section>
					<div className="flex items-center gap-3 mb-6">
						<span className="w-8 h-px bg-violet-500" />
						<span className="text-xs font-semibold text-violet-600 uppercase tracking-widest">
							Impact
						</span>
					</div>
					<h2 className="text-3xl font-bold mb-4">
						Une communauté qui s'emballe dès le lancement
					</h2>
					<p className="text-muted-foreground max-w-3xl mb-10 leading-relaxed">
						Le lancement de PlayRyvenox Gaming a généré un engouement immédiat
						dans la communauté. Les membres Discord ont massivement migré vers
						la plateforme, attirés par les profils, les classements et les
						premiers tournois organisés dès la semaine du lancement.
					</p>
					<div className="grid md:grid-cols-2 gap-6">
						{[
							{
								metric: "2 800+",
								detail: "membres actifs en 3 mois",
								desc: "La plateforme a attiré plus de 2 800 membres actifs dans les trois mois suivant le lancement — bien au-delà des objectifs initiaux.",
								color: "text-violet-600",
								bg: "from-violet-50 to-purple-50",
								border: "border-violet-100",
							},
							{
								metric: "140+",
								detail: "tournois organisés",
								desc: "Le système de tournois a permis d'organiser plus de 140 compétitions sur tous les jeux — là où une dizaine par an était possible avec l'organisation manuelle.",
								color: "text-amber-600",
								bg: "from-amber-50 to-yellow-50",
								border: "border-amber-100",
							},
							{
								metric: "28 min",
								detail: "de temps moyen par session",
								desc: "Un engagement remarquable pour une plateforme communautaire — porté par les classements, les profils et les événements qui donnent envie de rester.",
								color: "text-blue-600",
								bg: "from-blue-50 to-sky-50",
								border: "border-blue-100",
							},
							{
								metric: "92%",
								detail: "de taux de rétention à M+1",
								desc: "92% des membres inscrits reviennent le mois suivant — signe que la plateforme génère une vraie valeur quotidienne pour la communauté.",
								color: "text-emerald-600",
								bg: "from-emerald-50 to-green-50",
								border: "border-emerald-100",
							},
						].map((r) => (
							<div
								key={r.metric}
								className={`bg-gradient-to-br ${r.bg} border ${r.border} rounded-2xl p-8`}
							>
								<div className={`text-5xl font-bold mb-2 ${r.color}`}>
									{r.metric}
								</div>
								<div className="font-semibold text-foreground mb-3">
									{r.detail}
								</div>
								<p className="text-sm text-muted-foreground leading-relaxed">
									{r.desc}
								</p>
							</div>
						))}
					</div>
				</section>

				{/* ── Citation ── */}
				<section className="border border-violet-100 bg-gradient-to-br from-violet-50 to-blue-50 rounded-2xl p-10 text-center">
					<span className="text-4xl mb-6 block">🎮</span>
					<blockquote className="text-xl font-medium text-foreground max-w-2xl mx-auto mb-6 leading-relaxed">
						"On avait une communauté géniale mais on manquait d'outils pour la
						faire grandir. Depuis la plateforme, c'est une autre dimension — les
						membres sont beaucoup plus impliqués et on peut enfin organiser de
						vrais tournois."
					</blockquote>
					<cite className="text-sm text-muted-foreground not-italic">
						Fondateur PlayRyvenox Gaming
					</cite>
				</section>

				{/* ── Conclusion + CTA ── */}
				<section>
					<div className="flex items-center gap-3 mb-6">
						<span className="w-8 h-px bg-violet-500" />
						<span className="text-xs font-semibold text-violet-600 uppercase tracking-widest">
							Conclusion
						</span>
					</div>
					<h2 className="text-3xl font-bold mb-6">
						Une communauté gaming mérite une plateforme à sa hauteur
					</h2>
					<div className="max-w-3xl space-y-4 text-muted-foreground leading-relaxed mb-12">
						<p>
							PlayRyvenox Gaming démontre que derrière chaque communauté Discord
							passive se cache une communauté active en attente d'une vraie
							plateforme. Donner aux membres les bons outils — profils,
							tournois, classements — libère un potentiel d'engagement
							considérable.
						</p>
						<p>
							Pour les créateurs de communautés gaming, investir dans une
							plateforme propre est souvent le passage obligé vers la
							professionnalisation : meilleure expérience membre, possibilités
							de monétisation, attractivité pour les sponsors et capacité à
							organiser des événements de grande envergure.
						</p>
						<p>
							C'est cette vision que Weblax a contribué à concrétiser pour
							PlayRyvenox — et ce que nous pouvons faire pour votre communauté.
						</p>
					</div>

					<div className="bg-foreground text-background rounded-2xl p-10 flex flex-col md:flex-row items-center justify-between gap-6">
						<div>
							<h3 className="text-xl font-bold mb-2">
								Votre communauté mérite sa propre plateforme
							</h3>
							<p className="text-sm opacity-70">
								Plateforme sur mesure, app web, espace communautaire — parlons
								de votre projet.
							</p>
						</div>
						<a
							href="/contact"
							className="shrink-0 bg-white text-foreground font-semibold px-8 py-3 rounded-xl hover:bg-violet-50 transition-colors text-sm whitespace-nowrap"
						>
							Démarrer un projet →
						</a>
					</div>
				</section>
			</main>
		</>
	);
}
